import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { publishProduct } from '@dukanti/shared';
import type { PublishProductInput, PublishProductResult, ProductWithImages } from '@dukanti/shared';
import { supabase } from '@/lib/supabase';
import { productKeys } from './use-products';

/**
 * Publish a single product via `publish_product(uuid, true)`.
 *
 * ## Cache shape contract (LOAD-BEARING)
 *
 * The RPC returns the full `Product` (scalar) row — which is the
 * shape of `PublishProductResult`. But the detail query `useProduct`
 * hydrates with `ProductWithImages` (joins product_images). Writing
 * the raw RPC row into `productKeys.detail(productId)` would DROP
 * the `images` array and break gallery + price rendering on the
 * next render.
 *
 * So: on success we READ the current cached entry, merge the RPC's
 * scalar columns over it while preserving `images` (and any future
 * nested fields), and write back. If there's no cached entry (rare —
 * user hit Publish from a freshly-loaded list page without the
 * detail route ever touching the cache), we skip the setQueryData
 * update and rely solely on invalidateQueries(productKeys.list(storeId))
 * to refetch.
 *
 * This is the per-product equivalent of F-004's M1 store cache
 * contract in `use-publish-store.ts`.
 *
 * ## List invalidation
 *
 * We also invalidate `productKeys.list(storeId)` so the product card
 * in the list grid reflects the new `published` state on next render
 * (the card has a draft badge driven by `!product.published`). The
 * storeId is NOT on `PublishProductInput` — we read it from the RPC
 * return row's `store_id` field.
 *
 * ## Error behavior
 *
 * Errors are re-thrown; the dialog runs `mapPublishProductError(err)`
 * itself. Same split as use-publish-store.ts / publish-store-dialog.
 */
export function usePublishProduct(): UseMutationResult<
  PublishProductResult,
  unknown,
  PublishProductInput
> {
  const queryClient = useQueryClient();

  return useMutation<PublishProductResult, unknown, PublishProductInput>({
    mutationFn: (input) => publishProduct(supabase, input),
    onSuccess: (row, variables) => {
      // Merge the RPC scalar columns into the cached ProductWithImages
      // entry. If the cache miss case hits (existing === undefined) we
      // return undefined — the list invalidation below ensures the
      // list grid still refetches and the detail route hydrates from
      // scratch on next navigation.
      queryClient.setQueryData<ProductWithImages | undefined>(
        productKeys.detail(variables.productId),
        (existing) => (existing ? { ...existing, ...row } : undefined),
      );

      // List invalidation uses the RPC row's store_id (not variables)
      // because PublishProductInput intentionally omits store_id.
      void queryClient.invalidateQueries({
        queryKey: productKeys.list(row.store_id),
      });
      void queryClient.invalidateQueries({ queryKey: ['billing', 'entitlement', row.store_id] });
    },
  });
}
